import { useRouter } from 'next/router';
import qs from 'qs';
import { useCallback, useSyncExternalStore } from 'react';

// Both development and production compile catalogs before starting Next.js.
// Keep the legacy second argument for existing page loaders during migration.
const translationCatalogs = {
  en: () => import("./translations/locales/en/messages"),
  "nl-be": () => import("./translations/locales/nl-be/messages"),
  "tr-tr": () => import("./translations/locales/tr-tr/messages"),
  pseudo: () => import("./translations/locales/pseudo/messages"),
};

export async function loadTranslation(locale: string, _isProduction = true) {
  if (!Object.prototype.hasOwnProperty.call(translationCatalogs, locale)) {
    throw new Error(`Unsupported translation locale: ${locale}`);
  }

  const data = await translationCatalogs[locale as keyof typeof translationCatalogs]();
  return data.messages;
}

/**
 * Helper to make GET requests to Strapi API endpoints
 * @param {string} path Path of the API route
 * @param {Object} urlParamsObject URL params object, will be stringified
 * @param {Object} options Options passed to fetch
 * @returns Parsed API call response
 */
export async function fetchAPI(
  path: string,
  urlParamsObject = {},
  options = {}
) {
  // Merge default and user options
  const mergedOptions = {
    headers: {
      "Content-Type": "application/json",
    },
    ...options,
  };

  // Build request URL
  const queryString = qs.stringify(urlParamsObject);

  const requestUrl = `${
    process.env.NEXT_PUBLIC_STRAPI_API_URL +
    `/api${path}${queryString ? `?${queryString}` : ""}`
  }`;

  // Trigger API call
  const response = await fetch(requestUrl, mergedOptions);

  // Handle response
  if (!response.ok) {
    console.error(response.statusText);
  }
  const data = await response.json();
  return data;
}

export async function fetchQuery(
  path: string,
  urlParamsObject = {},
  options = {}
) {
  // Merge default and user options
  const mergedOptions = {
    headers: {
      "Content-Type": "application/json",
    },
    ...options,
  };

  // Build request URL
  const queryString = qs.stringify(urlParamsObject, { encodeValuesOnly: true });

  const requestUrl = `${
    process.env.NEXT_PUBLIC_STRAPI_API_URL +
    `/api${path}${queryString ? `?${queryString}` : ""}`
  }`;

  // Trigger API call
  return fetch(requestUrl, mergedOptions).then((res) => res.json());
}

export function getStrapiMedia(media: any) {
  if (media.data) {
    const { url } = media.data.attributes;
    // const imageUrl = url;
    const imageUrl = url.startsWith("/")
      ? process.env.NEXT_PUBLIC_STRAPI_API_URL + url
      : url;
    return imageUrl;
  }
  https: return "";
}

//www.digifist.com/_next/image?url=https%3A%2F%2Fcmsad.digifist.com%2Fuploads%2FFrame_991893_min_cfeaa1f10f.jpg&w=3840&q=75

export function getStrapiMediaArr(media: any) {
  const { url } = media.attributes;
  const imageUrl = url.startsWith("/")
    ? process.env.NEXT_PUBLIC_STRAPI_API_URL + url
    : url;
  return imageUrl;
}

export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

export const payloadPopulate = {
  Sections: {
    populate: {
      Image2: "*",
      Link: {
        populate: "*",
      },
      Link1: {
        populate: "*",
      },
      ImageTabs: {
        populate: "*",
      },
      StackPartners: {
        populate: "*",
      },
      PromiseBoxes: {
        populate: "*",
      },
      Link2: {
        populate: "*",
      },
      Box: {
        populate: "*",
      },
      SizeBox: {
        populate: "*",
      },
      Text: "*",
      Logos: {
        populate: "*",
      },
      LogosImages: "*",
      Tab: "*",
      Project: {
        populate: {
          project: {
            populate: "*",
          },
        },
      },
      slider: {
        populate: {
          Slide: {
            populate: {
              Title: "*",
              Subtitle: "*",
              Badge: "*",
              Image: "*",
              project: {
                populate: {
                  Badge: "*",
                  Image: "*",
                  localizations: "*",
                },
              },
              article: {
                populate: "*",
              },
            },
          },
        },
      },
      Slide: {
        populate: {
          Image: "*",
        },
      },
      Testimonials: "*",
      Image: {
        populate: "*",
      },
      VideoPlaceholder: {
        populate: "*",
      },
      Video: {
        populate: "*",
      },
      Accordion: {
        populate: "*",
      },
      Members: {
        populate: "*",
      },
      Button: {
        populate: "*",
      },
      Columns: {
        populate: "*",
      },
      Images: {
        populate: "*",
      },
      FormBuilder: {
        populate: "*",
      },
      FormField: {
        populate: "*",
      },
      ZapierHooks: {
        populate: "*",
      },
      IconCard: {
        populate: "*",
      },
      IconCardWithMedia: {
        populate: "*",
      },
      IconCardExtra: {
        populate: "*",
      },
      IconCardContent: {
        populate: "*",
      },
      ButtonIcon: "*",
      ButtonIconDownload: "*",
      Pricing: {
        populate: "*",
      },
      PriceCard: {
        populate: "*",
      },
      ScrollText: {
        populate: "*",
      },
      DownloadContent: "*",
      PercentCard: {
        populate: "*",
      },
      PercentCardContent: {
        populate: "*",
      },
      SingleImage: {
        populate: "*",
      },
      GTM_Event: {
        populate: "*",
      },
    },
  },
  localizations: "*",
  SEO: {
    populate: "*",
  },
};

export const fetchPayloadPage = (ctx: any) => ({
  populate: payloadPopulate,
  locale: ctx.locale,
  filters: {
    slug: ctx.params?.slug,
  },
});

export const getStaticPropsPage = async (ctx: any, url: string) => {
  const translation = await loadTranslation(
    ctx.locale!,
    process.env.NODE_ENV === "production"
  );
  const locale = getLocale(ctx.locale);

  const response = await fetchAPI(url, {
    populate: payloadPopulate,
    locale: locale,
    filters: {
      $or: [
        {
          slug: ctx.params?.slug,
        },
        {
          slug_overwrite: ctx.params?.slug,
        },
      ],
    },
  });

  if (response?.error || !Array.isArray(response?.data)) {
    throw new Error(`Cannot load published page: ${url}`);
  }
  const record = response.data.pop();
  if (!record) return { notFound: true as const };
  const { loadCanonicalPage } = await import("./lib/load-canonical-page");
  const normalized = await loadCanonicalPage(record, {
    fetchAPI, populate: payloadPopulate, locale, collection: url,
  });

  return {
    props: {
      translation,
      data: normalized,
    },
    revalidate: 10,
  };
};

export const articleDate = (date: Date) => {
  return new Date(date).toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

export const useMediaQuery = (width: number) => {
  const query = `(max-width: ${width}px)`;
  const subscribe = useCallback((notify: () => void) => {
    const media = window.matchMedia(query);
    media.addEventListener("change", notify);
    return () => media.removeEventListener("change", notify);
  }, [query]);
  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
};

export const getLocale = (local: any) => {
  const locales: any = {
    "nl-be": "nl-BE",
    en: "en",
    "tr-tr": "tr-TR",
    pseudo: "pseudo",
  };

  return locales[local] ? locales[local] : "en";
};

export const scrollToSection = (element: any) => {
  const { top } = element.getBoundingClientRect();
  const header = document.querySelector(".header");
  if (header && top) {
    window.scrollTo({
      top: window.scrollY + top - header.clientHeight,
      behavior: "smooth",
    });
  }
};

export const scrollToSectionAccordion = (element: any) => {
  const { top } = element.getBoundingClientRect();
  const header = document.querySelector(".header");
  let headerHeight = 0;
  if (header) {
    headerHeight = header.clientHeight;
  }
  const scrollTo = window.scrollY + top - headerHeight;
  if (header && top) {
    window.scrollTo({
      top: scrollTo,
      behavior: "smooth",
    });
  }
};

export const handleBaseLocales = (locales: any) =>
  locales
    .filter((item: string) => item !== "default")
    .map((item: string) => ({
      locale: item,
    }));

export const getSlug = (data: any) =>
  data.slug_overwrite || data.Slug || data.slug;

export const getLocalizationData = (
  data: any,
  pageSlug: string = "",
  relation: string = ""
) => {
  if (!data) {
    return null;
  }

  const localizationData: any = {};
  const otherAtts = data.attributes?.localizations?.data[0]?.attributes || null;

  localizationData["currentLangCode"] = data.attributes?.locale || null;
  localizationData["currentSlug"] =
    pageSlug || data.attributes?.slug_overwrite || data.attributes?.slug;

  if (otherAtts) {
    let relationSlug = "";
    let otherPageSlug = pageSlug || otherAtts.slug_overwrite || otherAtts.slug;

    if (relation) {
      const otherAppAtts = otherAtts[relation]?.data[0]?.attributes;
      if (otherAppAtts) {
        relationSlug = (otherAppAtts.slug_overwrite || otherAppAtts.slug) + "/";
      }
    }

    localizationData["anotherLocale"] = {
      [otherAtts.locale]: relationSlug + otherPageSlug,
    };
  }

  return localizationData;
};

export const useThemeString = () => {
  const router = useRouter();
  const themeString = router.locale === "nl-be" ? "shopify-themas" : "themes";
  return themeString;
};

export function capitalize(s: string) {
  return s.toLowerCase().replace(/\b./g, function (a) {
    return a.toUpperCase();
  });
}
